import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import anime from 'animejs/lib/anime.es';
import * as style from './ArticleAccordionSection.module.scss';
import { ReactComponent as ChevronRightIcon } from '../../../assets/vectors/Chevron-right.svg';
import { clearAllTagsFromString } from '../../../utilities';

const ArticleAccordionSection = (props) => {
  const {
    title,
    textMultiEntries,
    currentSectionIndex,
    activeSectionIndex,
    toggleSection,
    setIsAccordionAnimating,
  } = props;

  const accordionSectionId = useRef();
  const textDivId = useRef();
  const shouldBeCollapsed = currentSectionIndex !== activeSectionIndex;
  const [isCollapsed, setIsCollapsed] = useState(shouldBeCollapsed);

  let animation;

  if (typeof window !== 'undefined') {
    animation = (isCollapseAnimation) => {
      const textDivEl = textDivId.current;
      if (isCollapseAnimation) {
        anime({
          targets: textDivEl,
          height: 0,
          paddingTop: 0,
          duration: 500,
          easing: 'easeInOutCubic',
          complete() {
            setIsCollapsed(shouldBeCollapsed);
            setIsAccordionAnimating(false);
          },
        });
      } else {
        textDivEl.style.height = 'auto';
        const height = textDivEl.clientHeight;
        textDivEl.style.height = 0;
        anime({
          targets: textDivEl,
          height: [0, height],
          paddingTop: 20,
          duration: 500,
          easing: 'easeInOutCubic',
          complete() {
            setIsCollapsed(shouldBeCollapsed);
            setIsAccordionAnimating(false);
          },
        });
      }
    };
  }

  useEffect(() => {
    if (isCollapsed !== shouldBeCollapsed) {
      if (typeof window !== 'undefined' && animation) {
        animation(shouldBeCollapsed);
      }
    }
  }, [shouldBeCollapsed]);

  return (
    <div className={style.accordionSection} ref={accordionSectionId}>
      <div
        className={style.titleWrapper}
        onClick={() => {
          toggleSection(currentSectionIndex);
        }}
      >
        <div className={style.title}>{title}</div>
        {/* TODO: animate Chevron */}
        <ChevronRightIcon width={20} height={20} />
      </div>
      <div
        className={`${style.text} ${isCollapsed ? style.collapsed : null}`}
        ref={textDivId}
      >
        {textMultiEntries?.map((textMultiEntry, index) => {
          if (textMultiEntry?.accordionTextAdvancedTocLink) {
            return (
              <a
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                href={textMultiEntry?.accordionTextAdvancedTocLink?.url}
                target={textMultiEntry?.accordionTextAdvancedTocLink?.target}
                className="d-block"
                title={textMultiEntry?.accordionTextAdvancedTocLink?.title}
              >
                {clearAllTagsFromString(textMultiEntry?.accordionText) ||
                  textMultiEntry?.accordionTextAdvancedTocLink?.title}
              </a>
            );
          }
          return (
            // eslint-disable-next-line react/no-array-index-key
            <p key={index}>
              {clearAllTagsFromString(textMultiEntry?.accordionText)}
            </p>
          );
        })}
      </div>
    </div>
  );
};

ArticleAccordionSection.propTypes = {
  /** Title of an Accordion Section. */
  title: PropTypes.string.isRequired,
  /** Array of Accordion section contents in format (text + optional link) */
  textMultiEntries: PropTypes.arrayOf(
    PropTypes.shape({
      accordionTextAdvancedTocLink: PropTypes.shape({
        target: PropTypes.string,
        title: PropTypes.string,
        url: PropTypes.string,
      }),
      accordionText: PropTypes.string,
    })
  ).isRequired,
  /** Index of current section in accordion. */
  currentSectionIndex: PropTypes.number.isRequired,
  /** Index of accordion active section. */
  activeSectionIndex: PropTypes.number.isRequired,
  /** Function to handle clicks on an Accordion Section. */
  toggleSection: PropTypes.func.isRequired,
  /** A function to set according state to isAnimating: true/false. */
  setIsAccordionAnimating: PropTypes.func.isRequired,
};

export default ArticleAccordionSection;
